/* =========================================================
   Vendor Onboarding Form — shadow-DOM aware styling + logo
   The Shopify <shopify-forms-embed> web component renders the
   entire form inside its open shadow root, so external CSS
   cannot reach it. This script:
     1. Locates each <shopify-forms-embed>
     2. Waits for its shadow root + form-wrapper section
     3. Injects our CSS as <style> inside the shadow root
     4. Prepends the logo / subtitle / required-note into the
        form header inside the shadow root
     5. Sets a "Your answer" placeholder on empty inputs
     6. Replaces <legend> with a <div> so it renders inside the card
     7. Wires required-field validation (red border + message on blur)
     8. Optionally wraps the form in a 2-column layout with a
        sticky rich-text sidebar
   ========================================================= */
(function () {
  var CONFIG = window.vendorFormConfig || {};
  var LOGO_URL = CONFIG.logoUrl || '';
  var SUBTITLE = CONFIG.subtitle || '';
  var REQUIRED_NOTE = CONFIG.requiredNote || '';
  var CSS_URL = CONFIG.cssUrl || '';
  var SHOW_SIDEBAR = !!CONFIG.showSidebar;
  var SIDEBAR_HTML = (CONFIG.sidebarHtml || '').trim();
  var SHOW_RICHTEXT_MOBILE = !!CONFIG.showRichtextMobile;
  var SIDE_IMG_DESKTOP = CONFIG.sidebarImageDesktop || '';
  var SIDE_IMG_MOBILE = CONFIG.sidebarImageMobile || '';
  var BADGES = (CONFIG.badges || []).filter(function (b) { return b && String(b).trim() !== ''; });

  var cssPromise = null;

  function loadCss() {
    if (cssPromise) return cssPromise;
    if (!CSS_URL) return Promise.resolve('');
    cssPromise = fetch(CSS_URL)
      .then(function (r) { return r.text(); })
      .catch(function () { return ''; });
    return cssPromise;
  }

  function injectIntoHeader(header) {
    if (!header || header.dataset.vendorLogoInjected === '1') return;

    if (LOGO_URL) {
      var img = document.createElement('img');
      img.src = LOGO_URL;
      img.alt = 'Logo';
      img.className = 'custom-vendor-logo';
      img.loading = 'lazy';
      header.insertBefore(img, header.firstChild);
    }

    if (SUBTITLE) {
      var sub = document.createElement('p');
      sub.className = 'custom-vendor-subtitle';
      sub.textContent = SUBTITLE;
      header.appendChild(sub);
    }

    if (REQUIRED_NOTE) {
      var note = document.createElement('p');
      note.className = 'custom-vendor-required-note';
      note.textContent = REQUIRED_NOTE;
      header.appendChild(note);
    }

    header.dataset.vendorLogoInjected = '1';
  }

  function ensureStyleInShadow(root) {
    if (!root || root.querySelector('style[data-vendor-form-style]')) return;
    loadCss().then(function (css) {
      if (!css) return;
      if (root.querySelector('style[data-vendor-form-style]')) return;
      var style = document.createElement('style');
      style.setAttribute('data-vendor-form-style', '1');
      style.textContent = css;
      root.appendChild(style);
    });
  }

  function setPlaceholders(wrapper) {
    var inputs = wrapper.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], input:not([type])'
    );
    inputs.forEach(function (inp) {
      if (inp.placeholder && inp.placeholder.trim() !== '') return;
      if (inp.type === 'checkbox' || inp.type === 'file' || inp.type === 'hidden') return;
      inp.placeholder = 'Your answer';
    });
  }

  /* Browsers render <legend> in a special way — usually positioned on the
     <fieldset> border, outside the visual card background. Replace each
     legend with a <div> that flows normally inside the fieldset. */
  function fixLegends(wrapper) {
    var legends = wrapper.querySelectorAll('legend[class*="_checkboxLegend"]');
    legends.forEach(function (legend) {
      if (legend.dataset.vendorMoved === '1') return;
      var div = document.createElement('div');
      div.className = 'custom-vendor-legend';
      div.textContent = legend.textContent;
      legend.parentNode.insertBefore(div, legend);
      legend.style.display = 'none';
      legend.dataset.vendorMoved = '1';
    });
  }

  /* ---------- Required-field validation (Clinikally-style) ----------
     For any card whose label/legend contains '*', show a red border +
     "This is a required question" error when the user moves focus away
     while leaving the field empty. */
  var ERROR_ICON_SVG = '<svg class="vendor-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 7v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="17" r="1" fill="currentColor"/></svg>';

  function cardIsRequired(card) {
    var label = card.querySelector('[class*="_formInputFieldLabel"]');
    if (label && label.textContent.indexOf('*') !== -1) return true;
    var legend = card.querySelector('.custom-vendor-legend, legend[class*="_checkboxLegend"]');
    if (legend && legend.textContent.indexOf('*') !== -1) return true;
    return false;
  }

  function cardHasValue(card) {
    var inputs = card.querySelectorAll(
      'input[class*="_formInputField"], input[class*="_formPhoneInputField"], input[type="checkbox"]'
    );
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (inp.type === 'checkbox') {
        if (inp.checked) return true;
      } else if (inp.value && inp.value.trim() !== '') {
        return true;
      }
    }
    return false;
  }

  function showError(card) {
    card.classList.add('vendor-error');
    if (card.querySelector('.vendor-error-message')) return;
    var msg = document.createElement('div');
    msg.className = 'vendor-error-message';
    msg.innerHTML = ERROR_ICON_SVG + '<span>This is a required question</span>';
    card.appendChild(msg);
  }

  function clearError(card) {
    card.classList.remove('vendor-error');
    var msg = card.querySelector('.vendor-error-message');
    if (msg) msg.remove();
  }

  function setupValidation(wrapper) {
    var cards = wrapper.querySelectorAll('[class*="_formFieldContainer"], [class*="_checkboxFieldSet"]');
    cards.forEach(function (card) {
      if (card.dataset.vendorValBound === '1') return;
      if (!cardIsRequired(card)) return;

      var inputs = card.querySelectorAll(
        'input[class*="_formInputField"], input[class*="_formPhoneInputField"], input[type="checkbox"]'
      );
      if (inputs.length === 0) return;

      inputs.forEach(function (inp) {
        inp.addEventListener('focus', function () {
          clearError(card);
        });
        inp.addEventListener('blur', function () {
          setTimeout(function () {
            if (!cardHasValue(card)) {
              showError(card);
            } else {
              clearError(card);
            }
          }, 60);
        });
        if (inp.type === 'checkbox') {
          inp.addEventListener('change', function () {
            if (cardHasValue(card)) clearError(card);
          });
        }
      });

      card.dataset.vendorValBound = '1';
    });
  }

  function processRoot(root) {
    var wrapper = root.querySelector('section[data-sizing="form-wrapper"]');
    if (!wrapper) return false;
    ensureStyleInShadow(root === document ? document.head : root);
    var header = wrapper.querySelector('[class*="_formHeader"]');
    if (header) injectIntoHeader(header);
    setPlaceholders(wrapper);
    fixLegends(wrapper);
    setupValidation(wrapper);
    return true;
  }

  var HOST_TAG = 'shopify-forms-embed';

  /* position:sticky breaks if ANY ancestor up the tree has
     overflow != visible. Walk up from the wrapper to <body> and force
     overflow: visible on each ancestor that uses hidden/auto/scroll. */
  function unlockStickyAncestors(node) {
    var el = node.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      var cs = window.getComputedStyle(el);
      if (cs.overflow !== 'visible' || cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('overflow-x', 'visible', 'important');
        el.style.setProperty('overflow-y', 'visible', 'important');
      }
      el = el.parentElement;
    }
  }

  function buildSidebarImage() {
    if (!SIDE_IMG_DESKTOP && !SIDE_IMG_MOBILE) return null;
    var pic = document.createElement('picture');
    pic.className = 'vendor-form-sidebar-image';

    var desktopUrl = SIDE_IMG_DESKTOP || SIDE_IMG_MOBILE;
    var mobileUrl = SIDE_IMG_MOBILE || SIDE_IMG_DESKTOP;

    if (mobileUrl && mobileUrl !== desktopUrl) {
      var src = document.createElement('source');
      src.media = '(max-width: 768px)';
      src.srcset = mobileUrl;
      pic.appendChild(src);
    }
    var img = document.createElement('img');
    img.src = desktopUrl;
    img.alt = '';
    img.loading = 'lazy';
    pic.appendChild(img);
    return pic;
  }

  /* 4 themed icons that cycle through the badges — shield, truck,
     support headset, lightning. White stroke on a maroon gradient bg. */
  var BADGE_ICONS = [
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l8 3v6c0 5-3.5 9.5-8 11-4.5-1.5-8-6-8-11V5l8-3z" stroke="#fff" stroke-width="1.7" stroke-linejoin="round"/><path d="M8.5 12l2.5 2.5L16 9.5" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 7h11v9H3V7z" stroke="#fff" stroke-width="1.7" stroke-linejoin="round"/><path d="M14 10h4l3 3v3h-7v-6z" stroke="#fff" stroke-width="1.7" stroke-linejoin="round"/><circle cx="7" cy="18" r="1.7" stroke="#fff" stroke-width="1.7"/><circle cx="17" cy="18" r="1.7" stroke="#fff" stroke-width="1.7"/></svg>',
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 13a7 7 0 1 1 14 0v3a2 2 0 0 1-2 2h-1v-6h3" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 13v3h3v-6H5" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 20a2 2 0 0 0 2-2" stroke="#fff" stroke-width="1.7" stroke-linecap="round"/></svg>',
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" stroke="#fff" stroke-width="1.7" stroke-linejoin="round" fill="rgba(255,255,255,0.12)"/></svg>'
  ];

  function buildBadges() {
    if (!BADGES.length) return null;
    var card = document.createElement('div');
    card.className = 'vendor-form-sidebar-badges';
    var ul = document.createElement('ul');
    BADGES.forEach(function (text, idx) {
      var icon = BADGE_ICONS[idx % BADGE_ICONS.length];
      var li = document.createElement('li');
      li.innerHTML =
        '<span class="vendor-form-badge-icon">' + icon + '</span>' +
        '<span class="vendor-form-badge-text">' + String(text).replace(/[<>]/g, '') + '</span>';
      ul.appendChild(li);
    });
    card.appendChild(ul);
    return card;
  }

  /* Wrap the <shopify-forms-embed> with a 2-column layout: sidebar on the
     LEFT (rich text + optional image, sticky), form on the RIGHT.
     On mobile (≤768px) it stacks: sidebar text → image → form. */
  function setupSidebar() {
    if (!SHOW_SIDEBAR || !SIDEBAR_HTML) return;
    var hosts = document.querySelectorAll(HOST_TAG);
    hosts.forEach(function (host) {
      if (host.dataset.vendorWrapped === '1') {
        var existingWrap = host.closest('.vendor-form-layout');
        if (existingWrap) unlockStickyAncestors(existingWrap);
        return;
      }
      if (host.parentNode && host.parentNode.classList && host.parentNode.classList.contains('vendor-form-layout')) {
        host.dataset.vendorWrapped = '1';
        unlockStickyAncestors(host.parentNode);
        return;
      }
      var wrapper = document.createElement('div');
      wrapper.className = 'vendor-form-layout';

      var sidebar = document.createElement('aside');
      sidebar.className = 'vendor-form-sidebar';

      var textBox = document.createElement('div');
      textBox.className = 'vendor-form-sidebar-text';
      if (!SHOW_RICHTEXT_MOBILE) textBox.classList.add('vendor-hide-on-mobile');
      textBox.innerHTML = SIDEBAR_HTML;
      sidebar.appendChild(textBox);

      var sideImg = buildSidebarImage();
      if (sideImg) sidebar.appendChild(sideImg);

      var badges = buildBadges();
      if (badges) sidebar.appendChild(badges);

      var formCol = document.createElement('div');
      formCol.className = 'vendor-form-col';

      // Sidebar first (left on desktop, top on mobile), then form
      host.parentNode.insertBefore(wrapper, host);
      wrapper.appendChild(sidebar);
      wrapper.appendChild(formCol);
      formCol.appendChild(host);

      host.dataset.vendorWrapped = '1';
      unlockStickyAncestors(wrapper);
    });
  }

  function scan() {
    setupSidebar();
    var hosts = document.querySelectorAll(HOST_TAG);
    hosts.forEach(function (host) {
      var sr = host.shadowRoot;
      if (!sr) return;
      processRoot(sr);
    });
    processRoot(document);
  }

  function init() {
    scan();
    var observer = new MutationObserver(function () { scan(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setInterval(scan, 700);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
